/**
 * LobbyScreen — Composes the four lobby sections and binds to LobbyService.
 *
 * Sections (top to bottom):
 *   1. Header (title)
 *   2. Player showcase (carousel)
 *   3. Run a Room section header + Create pill
 *   4. Room list
 *   5. Public chat (bottom sheet)
 *
 * The screen owns no data of its own — it reads from LobbyService and
 * patches its child components on service events. When the server lands,
 * LobbyService's internals change but this screen does not.
 */

import { EventEmitter } from '../../engine/core/EventEmitter';
import { PlayerShowcase } from '../components/lobby/PlayerShowcase';
import { CreateRoomPill } from '../components/lobby/CreateRoomPill';
import { RoomList } from '../components/lobby/RoomList';
import { LobbyChat } from '../components/lobby/LobbyChat';
import { PlayersGridOverlay } from '../components/lobby/PlayersGridOverlay';
import type { LobbyService, ChatMessage, LobbyPlayer, LobbyRoom } from '../../services/LobbyService';
import type { RingColor } from '../components/profile/PlayerProfileBadge';

export interface LobbyScreenLocalUser {
  id: string;
  username: string;
  ringColor: RingColor;
}

interface LobbyScreenEvents {
  'profile:tap': { playerId: string; username?: string; picture?: string };
  'create_room:tap': void;
  'join_room:tap': { roomId: string };
  'back:tap': void;
}

export class LobbyScreen extends EventEmitter<LobbyScreenEvents> {
  public element: HTMLElement;

  private service: LobbyService;
  private localUser: LobbyScreenLocalUser;

  private showcase: PlayerShowcase;
  private createPill: CreateRoomPill;
  private roomList: RoomList;
  private chat: LobbyChat;
  private playersOverlay: PlayersGridOverlay;

  // Bound listeners (kept for clean teardown)
  private onChatMessage = (e: { message: ChatMessage }) => this.chat.appendMessage(e.message);
  private onPlayersUpdated = (e: { players: LobbyPlayer[] }) => {
    this.showcase.setPlayers(e.players);
    // Keep the grid overlay in sync if it's currently open.
    this.playersOverlay.setPlayers(e.players);
  };
  private onRoomsUpdated = () => this.roomList.setRooms(this.service.listRooms());
  private onRoomChanged = (e: { room: LobbyRoom }) => this.roomList.updateRoom(e.room);

  constructor(service: LobbyService, localUser: LobbyScreenLocalUser) {
    super();
    LobbyScreen.injectStyles();

    this.service = service;
    this.localUser = localUser;

    // Build DOM. UIManager.registerScreen overwrites className and sets
    // data-screen="lobby" — we target that attribute below in our styles.
    this.element = document.createElement('div');
    this.element.innerHTML = `
      <header class="sdl-lobby-header">
        <button class="sdl-lobby-back" type="button" aria-label="Back" data-back>←</button>
        <div class="sdl-lobby-title-block">
          <div class="sdl-lobby-crumb">LOBBY</div>
          <div class="sdl-lobby-title">STREET LOBBY</div>
        </div>
      </header>
      <div class="sdl-lobby-scroll" data-scroll>
        <div data-showcase></div>
        <div class="sdl-lobby-rooms-head">
          <div class="sdl-lobby-section-label">RUN A <span class="sdl-lobby-accent">ROOM</span></div>
          <div data-create-pill></div>
        </div>
        <div data-room-list></div>
      </div>
      <div data-chat></div>
    `;

    // Mount child components
    this.showcase = new PlayerShowcase();
    this.createPill = new CreateRoomPill('CREATE');
    this.roomList = new RoomList((id) => this.service.getPlayer(id));
    this.chat = new LobbyChat();
    this.chat.setLocalPlayerId(this.localUser.id);
    this.playersOverlay = new PlayersGridOverlay();
    document.body.appendChild(this.playersOverlay.element);

    (this.element.querySelector('[data-showcase]') as HTMLElement).appendChild(this.showcase.element);
    (this.element.querySelector('[data-create-pill]') as HTMLElement).appendChild(this.createPill.element);
    (this.element.querySelector('[data-room-list]') as HTMLElement).appendChild(this.roomList.element);
    (this.element.querySelector('[data-chat]') as HTMLElement).appendChild(this.chat.element);

    const backBtn = this.element.querySelector('[data-back]') as HTMLElement;
    backBtn.addEventListener('click', () => this.emit('back:tap', undefined));

    this.bindChildren();
    this.bindService();
    this.hydrate();
  }

  /** Update which user is "you" (e.g. after auth completes / username change). */
  setLocalUser(user: LobbyScreenLocalUser): void {
    this.localUser = user;
    this.chat.setLocalPlayerId(user.id);
  }

  destroy(): void {
    this.service.off('chat:message', this.onChatMessage);
    this.service.off('players:updated', this.onPlayersUpdated);
    this.service.off('rooms:updated', this.onRoomsUpdated);
    this.service.off('room:changed', this.onRoomChanged);

    this.showcase.destroy();
    this.createPill.destroy();
    this.roomList.destroy();
    this.chat.destroy();
    this.playersOverlay.destroy();

    this.element.remove();
    this.removeAllListeners();
  }

  // ---------- Internal ----------

  private bindChildren(): void {
    this.showcase.on('profile:tap', (e) => this.emit('profile:tap', e));
    this.showcase.on('see_all:tap', () => this.playersOverlay.show(this.service.listPlayers()));
    this.playersOverlay.on('profile:tap', (e) => {
      // Keep the grid open behind the player card — it closes only via its X.
      this.emit('profile:tap', e);
    });
    this.roomList.on('profile:tap', (e) => this.emit('profile:tap', e));
    // Tapping a sender's avatar in public chat opens their player card too.
    this.chat.on('profile:tap', (e) => this.emit('profile:tap', e));
    this.roomList.on('join:tap', (e) => {
      this.service.requestJoinRoom(e.roomId);
      this.emit('join_room:tap', e);
    });
    this.createPill.on('tap', () => {
      this.service.requestCreateRoom();
      this.emit('create_room:tap', undefined);
    });
    this.chat.on('send', ({ text }) => {
      this.service.sendChat(text, this.localUser);
    });
  }

  private bindService(): void {
    this.service.on('chat:message', this.onChatMessage);
    this.service.on('players:updated', this.onPlayersUpdated);
    this.service.on('rooms:updated', this.onRoomsUpdated);
    this.service.on('room:changed', this.onRoomChanged);
  }

  /** Initial fill from current service state. */
  private hydrate(): void {
    this.showcase.setPlayers(this.service.listPlayers());
    this.roomList.setRooms(this.service.listRooms());
    this.chat.setMessages(this.service.listMessages());
  }

  // ---------- Styles ----------

  private static injectStyles(): void {
    if (document.getElementById('sdl-lobby-screen-styles')) return;
    const style = document.createElement('style');
    style.id = 'sdl-lobby-screen-styles';
    style.textContent = `
      /* Override default .sdl-screen scroll behavior: lobby has internal scroll + fixed chat.
         padding-bottom is zeroed because the lobby hides the nav — chat handles
         its own safe-area-inset internally. */
      .sdl-screen[data-screen="lobby"] {
        display: flex;
        flex-direction: column;
        overflow: hidden;
        padding-bottom: 0;
      }

      .sdl-lobby-header {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: calc(env(safe-area-inset-top) + 12px) 16px 12px;
        border-bottom: 1px solid var(--glass-border);
        background: linear-gradient(180deg, rgba(0,0,0,0.4), transparent);
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        flex-shrink: 0;
      }
      .sdl-lobby-back {
        all: unset;
        width: 38px; height: 38px;
        display: flex; align-items: center; justify-content: center;
        background: var(--concrete-light);
        border: 1px solid var(--glass-border);
        border-radius: 10px;
        color: white;
        font-size: 18px;
        cursor: pointer;
        transition: transform 0.15s ease, background 0.15s ease;
      }
      .sdl-lobby-back:active {
        transform: scale(0.92);
        background: var(--concrete-lighter);
      }
      .sdl-lobby-title-block { flex: 1; }
      .sdl-lobby-crumb {
        font-family: var(--font-body);
        font-size: 10px;
        letter-spacing: 2.5px;
        color: rgba(255,255,255,0.4);
        font-weight: 600;
      }
      .sdl-lobby-title {
        font-family: var(--font-display);
        font-size: 24px;
        letter-spacing: 3px;
        line-height: 1;
        background: linear-gradient(180deg, #fff, #c8c8d0);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .sdl-lobby-scroll {
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        padding-bottom: 4px;
        -webkit-overflow-scrolling: touch;
      }
      .sdl-lobby-scroll::-webkit-scrollbar { width: 0; }

      .sdl-lobby-rooms-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 18px 16px 10px;
      }
      .sdl-lobby-section-label {
        font-family: var(--font-display);
        font-size: 14px;
        letter-spacing: 3px;
        color: rgba(255,255,255,0.55);
      }
      .sdl-lobby-accent { color: var(--gold); }
    `;
    document.head.appendChild(style);
  }
}

